import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, assetUrl } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import PublicLayout from "@/components/PublicLayout";
import FeaturedStrips from "@/components/FeaturedStrips";
import EditorsPicks from "@/components/EditorsPicks";
import NatureFeed from "@/components/NatureFeed";
import ShareButton from "@/components/ShareButton";
import AiIntro from "@/components/AiIntro";
import { MagnifyingGlass, MapPin, SpinnerGap, Camera, ArrowRight, Images, MapTrifold } from "@phosphor-icons/react";

const HERO = "https://images.unsplash.com/photo-1604223190546-a43e4c7f29d7?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const PAGE = 24;

export default function PublicGallery({ home = false }) {
  const [sp] = useSearchParams();
  useSeo({
    title: home
      ? "PhotoPulse.pro — A living library of nature, places & cityscapes"
      : "Explore — PhotoPulse.pro",
    description: "Browse a growing, AI-organised gallery of nature, landscapes, sunsets and cityscapes from photographers around the world. Free to explore.",
    image: HERO,
  });

  const [photos, setPhotos] = useState([]);
  const [facets, setFacets] = useState({ total: 0, categories: [], places: [] });
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  const [category, setCategory] = useState("");
  const [place, setPlace] = useState("");
  const [tag, setTag] = useState("");
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const searchRef = useRef();

  useEffect(() => { api.get("/public/gallery/facets").then((r) => setFacets(r.data)).catch(() => {}); }, []);

  // Seed filters from the URL (e.g. /explore?category=Nature or ?place=Paris or ?tag=sunset or ?q=mountains)
  useEffect(() => {
    setTag(sp.get("tag") || "");
    setCategory(sp.get("category") || "");
    setPlace(sp.get("place") || "");
    const qq = sp.get("q") || "";
    if (qq) { setQ(qq); setTerm(qq); }
  }, [sp]);

  const load = useCallback(async (reset) => {
    setLoading(true);
    const off = reset ? 0 : offset;
    try {
      const { data } = await api.get("/public/gallery", {
        params: { q: term || undefined, category: category || undefined, place: place || undefined, tag: tag || undefined, limit: PAGE, offset: off },
      });
      setTotal(data.count);
      setPhotos((prev) => (reset ? data.photos : [...prev, ...data.photos]));
      setOffset(off + data.photos.length);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [term, category, place, tag, offset]);

  useEffect(() => { load(true); /* eslint-disable-next-line */ }, [term, category, place, tag]);

  const onSearch = (e) => { e.preventDefault(); setTerm(q.trim()); };
  const scrollToGrid = () => searchRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/70" />
        <div className="relative mx-auto max-w-5xl px-5 py-24 text-center text-white sm:py-32">
          <p className="overline text-white/80">A SocialPulse® network Media Library</p>
          <h1 className="mt-3 font-display text-4xl font-black leading-tight tracking-tight sm:text-5xl lg:text-6xl">
            Don't let your best photos die on your phone.
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base text-white/90 sm:text-lg">
            PhotoPulse turns forgotten camera rolls into a living, searchable library of nature, landscapes,
            sunsets and cityscapes — organised by AI, mapped by place, and shared with the world.
          </p>
          <form onSubmit={onSearch} className="mx-auto mt-8 flex max-w-xl items-center gap-2 rounded-full bg-white p-1.5 shadow-lg">
            <MagnifyingGlass size={20} weight="bold" className="ml-3 text-zinc-400" />
            <input data-testid="gallery-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search sunsets, mountains, a city…"
              className="flex-1 bg-transparent px-2 py-2 text-sm text-zinc-800 outline-none" />
            <button data-testid="gallery-search-btn" type="submit" className="rounded-full bg-[#F27A54] px-5 py-2 text-sm font-bold text-white hover:bg-[#d9633f]">Search</button>
          </form>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <button onClick={scrollToGrid} className="flex items-center gap-1.5 rounded-full bg-white/15 px-4 py-2 text-sm font-semibold backdrop-blur hover:bg-white/25">
              <Images size={16} weight="fill" /> Browse the gallery
            </button>
            <Link to="/login" data-testid="gallery-hero-contribute" className="flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-sm font-bold text-[#F27A54] hover:bg-white/90">
              <Camera size={16} weight="fill" /> Contribute your photos <ArrowRight size={15} weight="bold" />
            </Link>
          </div>
          {facets.total > 0 && <p className="mt-6 text-xs text-white/70">{facets.total.toLocaleString()} public photos and growing</p>}
        </div>
      </section>

      {/* FEATURED STRIPS (home only) */}
      {home && <EditorsPicks />}
      {home && <NatureFeed />}
      {home && <FeaturedStrips />}

      {/* FILTERS + GRID */}
      <section ref={searchRef} className="mx-auto max-w-6xl scroll-mt-20 px-5 py-12">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold">Browse the gallery</h2>
          <Link to="/explore/map" data-testid="gallery-to-map"
            className="flex items-center gap-1.5 rounded-full border border-[#F27A54] px-4 py-2 text-sm font-bold text-[#F27A54] hover:bg-blue-50">
            <MapTrifold size={16} weight="fill" /> Map view
          </Link>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="gallery-filters">
          <button onClick={() => { setCategory(""); setPlace(""); setTag(""); }}
            className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${!category && !place && !tag ? "bg-[#F27A54] text-white" : "bg-white text-zinc-600 ring-1 ring-border hover:ring-[#F27A54]"}`}>
            All
          </button>
          {tag && (
            <button data-testid="gallery-tag-active" onClick={() => setTag("")}
              className="flex items-center gap-1 rounded-full bg-[#F27A54] px-3.5 py-1.5 text-xs font-bold text-white">
              #{tag} <span className="opacity-70">✕</span>
            </button>
          )}
          {facets.categories.map((c) => (
            <button key={c.name} data-testid={`gallery-cat-${c.name}`} onClick={() => { setPlace(""); setCategory(category === c.name ? "" : c.name); }}
              className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${category === c.name ? "bg-[#F27A54] text-white" : "bg-white text-zinc-600 ring-1 ring-border hover:ring-[#F27A54]"}`}>
              {c.name} <span className="opacity-60">{c.count}</span>
            </button>
          ))}
          {facets.places.slice(0, 8).map((p) => (
            <button key={p.name} onClick={() => { setCategory(""); setPlace(place === p.name ? "" : p.name); }}
              className={`flex items-center gap-1 rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${place === p.name ? "bg-[#F27A54] text-white" : "bg-white text-zinc-600 ring-1 ring-border hover:ring-[#F27A54]"}`}>
              <MapPin size={11} weight="fill" /> {p.name}
            </button>
          ))}
        </div>

        {category && <AiIntro place={category} kind="category" label={`About ${category} photography`} className="mt-5" />}

        {term && <p className="mt-4 text-sm text-muted-foreground">Results for "<span className="font-semibold text-zinc-800">{term}</span>" · <button className="text-[#F27A54] hover:underline" onClick={() => { setQ(""); setTerm(""); }}>clear</button></p>}

        {photos.length === 0 && !loading && (
          <div className="mt-16 grid place-items-center text-center text-muted-foreground">
            <Images size={40} weight="light" className="text-zinc-300" />
            <p className="mt-3 font-display text-lg font-bold text-zinc-700">No photos here yet</p>
            <p className="text-sm">Try a different search, or be the first to <Link to="/login" className="text-[#F27A54] hover:underline">contribute</Link>.</p>
          </div>
        )}

        <div className="mt-6 columns-2 gap-4 sm:columns-3 lg:columns-4 [&>*]:mb-4" data-testid="gallery-grid">
          {photos.map((p) => (
            <Link key={p.id} to={`/p/${p.id}`} data-testid={`gallery-photo-${p.id}`}
              className="group relative block break-inside-avoid overflow-hidden rounded-lg bg-zinc-100 shadow-sm ring-1 ring-black/5">
              <img src={assetUrl(p.image_url)} alt={p.alt_text || p.title || "photo"} loading="lazy"
                className="w-full transition-transform duration-500 group-hover:scale-[1.04]" />
              <div className="absolute right-2 top-2 opacity-100 transition-opacity duration-300 md:opacity-0 md:group-hover:opacity-100">
                <ShareButton id={p.id} title={p.title} />
              </div>
              <div className="absolute inset-x-0 bottom-0 translate-y-2 bg-gradient-to-t from-black/75 to-transparent p-3 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                <p className="line-clamp-1 text-sm font-bold text-white">{p.title || "Untitled"}</p>
                {p.location?.city && <p className="flex items-center gap-1 text-xs text-white/80"><MapPin size={11} weight="fill" />{p.location.city}{p.location.country ? `, ${p.location.country}` : ""}</p>}
              </div>
            </Link>
          ))}
        </div>

        {loading && <div className="mt-8 grid place-items-center"><SpinnerGap size={30} className="animate-spin text-[#F27A54]" weight="bold" /></div>}

        {!loading && photos.length < total && (
          <div className="mt-8 grid place-items-center">
            <button data-testid="gallery-load-more" onClick={() => load(false)}
              className="rounded-full border border-[#F27A54] px-6 py-2.5 text-sm font-bold text-[#F27A54] hover:bg-blue-50">
              Load more ({(total - photos.length).toLocaleString()} more)
            </button>
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
